// src/pages/couriers/components/CourierDeliveryChargesModal.jsx
import React, { useState, useEffect } from 'react';
import { Banknote, X, Save, RefreshCw, Edit3, Package, Check } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useAuth } from '../../../hooks/useAuth';
import { getShippingZones } from '../../../services/api/shipping';
import {
  getCourierDeliveryCharges,
  updateCourierDeliveryCharges
} from '../../../services/api/businessCourier';
import modalStyles from './ConfigModal.module.css';
import shopStyles from '../../shops/CreateShopPage.module.css';
import styles from './CourierDeliveryChargesModal.module.css';

const FIELDS = {
  delivery: { amount: 'amount', extra: 'extraChargePerKg' },
  return: { amount: 'returnAmount', extra: 'returnExtraChargePerKg' }
};

const KINDS = Object.keys(FIELDS);

const toInputValue = (charge, field) => (charge ? parseFloat(charge[field]) : '');

const CourierDeliveryChargesModal = ({ isOpen, onClose, courier, onSaved }) => {
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const { user } = useAuth();

  const [shippingZones, setShippingZones] = useState([]);
  const [charges, setCharges] = useState([]);
  const [activeTab, setActiveTab] = useState('delivery');
  const [showExtraCharges, setShowExtraCharges] = useState({ delivery: false, return: false });
  const [bulkEditKind, setBulkEditKind] = useState(null);
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkExtraValue, setBulkExtraValue] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !courier) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setErrors({});
        setActiveTab('delivery');
        const [zonesResponse, chargesResponse] = await Promise.all([
          getShippingZones(),
          getCourierDeliveryCharges(courier.id)
        ]);
        const zones = zonesResponse || [];
        const existing = chargesResponse.deliveryCharges || [];

        setShippingZones(zones);
        setShowExtraCharges({
          delivery: existing.some(c => parseFloat(c.extraChargePerKg) > 0),
          return: existing.some(c => parseFloat(c.returnExtraChargePerKg) > 0)
        });
        setCharges(zones.map(zone => {
          const charge = existing.find(c => c.shippingZoneId === zone.id);
          return {
            shippingZoneId: zone.id,
            amount: toInputValue(charge, 'amount'),
            extraChargePerKg: toInputValue(charge, 'extraChargePerKg'),
            returnAmount: toInputValue(charge, 'returnAmount'),
            returnExtraChargePerKg: toInputValue(charge, 'returnExtraChargePerKg')
          };
        }));
      } catch (err) {
        console.error('Failed to load courier delivery charges:', err);
        error(err.response?.data?.message || t('couriers.deliveryChargesLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, courier]);

  if (!isOpen || !courier) return null;

  const clearError = (key) => {
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const setZoneField = (zoneId, field, value) => {
    setCharges(prev => prev.map(charge =>
      charge.shippingZoneId === zoneId ? { ...charge, [field]: value } : charge
    ));
  };

  const handleChargeChange = (kind, zoneId, value) => {
    setZoneField(zoneId, FIELDS[kind].amount, value === '' ? '' : parseFloat(value));
    clearError(`${kind}Charges`);
  };

  const handleExtraChange = (kind, zoneId, value) => {
    setZoneField(zoneId, FIELDS[kind].extra, value === '' ? '' : value);
    clearError(`${kind}Extra`);
  };

  const setAllAmounts = (kind, value) => {
    setCharges(prev => prev.map(charge => ({ ...charge, [FIELDS[kind].amount]: value })));
    clearError(`${kind}Charges`);
  };

  const toggleExtraCharges = (kind, checked) => {
    setShowExtraCharges(prev => ({ ...prev, [kind]: checked }));
    clearError(`${kind}Extra`);
  };

  const handleBulkEditOpen = (kind) => {
    setBulkEditValue('');
    setBulkExtraValue('');
    setBulkEditKind(kind);
  };

  const handleBulkEditApply = () => {
    const applyExtra = showExtraCharges[bulkEditKind] && bulkExtraValue !== '';
    const applyAmount = bulkEditValue !== '';
    if (applyExtra && (isNaN(bulkExtraValue) || parseFloat(bulkExtraValue) < 0)) {
      error(t('shops.extraChargesRequired'));
      return;
    }
    if ((!applyExtra || applyAmount) && (!applyAmount || isNaN(bulkEditValue) || parseFloat(bulkEditValue) < 0)) {
      error(t('shops.invalidBulkEditValue'));
      return;
    }
    const { amount, extra } = FIELDS[bulkEditKind];
    setCharges(prev => prev.map(charge => ({
      ...charge,
      ...(applyAmount ? { [amount]: parseFloat(bulkEditValue) } : {}),
      ...(applyExtra ? { [extra]: bulkExtraValue } : {})
    })));
    if (applyAmount) clearError(`${bulkEditKind}Charges`);
    if (applyExtra) clearError(`${bulkEditKind}Extra`);
    setBulkEditKind(null);
    success(t('shops.bulkEditApplied'));
  };

  const handleSetFreeAll = (kind) => {
    setAllAmounts(kind, 0);
    success(t('shops.freeDeliveryApplied'));
  };

  const validate = () => {
    const newErrors = {};
    KINDS.forEach(kind => {
      const { amount, extra } = FIELDS[kind];
      if (charges.some(charge => charge[amount] === '' || charge[amount] < 0)) {
        newErrors[`${kind}Charges`] = t('shops.deliveryChargesRequired');
      }
      if (showExtraCharges[kind] && charges.some(charge =>
        charge[extra] === '' || isNaN(charge[extra]) || parseFloat(charge[extra]) < 0
      )) {
        newErrors[`${kind}Extra`] = t('shops.extraChargesRequired');
      }
    });
    setErrors(newErrors);
    return KINDS.find(kind => newErrors[`${kind}Charges`] || newErrors[`${kind}Extra`]) || null;
  };

  const handleSave = async () => {
    const kindWithErrors = validate();
    if (kindWithErrors) {
      setActiveTab(kindWithErrors);
      return;
    }

    const extraOrZero = (kind, charge) =>
      showExtraCharges[kind] ? (parseFloat(charge[FIELDS[kind].extra]) || 0) : 0;

    try {
      setIsSubmitting(true);
      await updateCourierDeliveryCharges(courier.id, charges.map(charge => ({
        shippingZoneId: charge.shippingZoneId,
        amount: parseFloat(charge.amount) || 0,
        extraChargePerKg: extraOrZero('delivery', charge),
        returnAmount: parseFloat(charge.returnAmount) || 0,
        returnExtraChargePerKg: extraOrZero('return', charge)
      })));
      success(t('couriers.deliveryChargesSaved'));
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to save courier delivery charges:', err);
      error(err.response?.data?.message || t('couriers.deliveryChargesSaveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderZoneInputs = (kind, fieldKey) => {
    const field = FIELDS[kind][fieldKey];
    const errorKey = fieldKey === 'amount' ? `${kind}Charges` : `${kind}Extra`;
    return (
      <div className={shopStyles.deliveryChargesGrid}>
        {shippingZones.map((zone) => {
          const value = charges.find(c => c.shippingZoneId === zone.id)?.[field];
          const inputId = `courier-${kind}-${fieldKey}-${zone.id}`;
          return (
            <div key={zone.id} className={shopStyles.chargeInputGroup}>
              <label htmlFor={inputId}>
                {zone.name} <span className={shopStyles.required}>*</span>
              </label>
              <div className={shopStyles.inputWithIcon}>
                <Banknote size={18} className={shopStyles.inputIcon} />
                <input
                  type="number"
                  id={inputId}
                  value={value === 0 ? 0 : (value || '')}
                  onChange={(e) => fieldKey === 'amount'
                    ? handleChargeChange(kind, zone.id, e.target.value)
                    : handleExtraChange(kind, zone.id, e.target.value)}
                  placeholder={fieldKey === 'amount' ? '150.00' : t('shops.extraChargePerKgPlaceholder')}
                  min="0"
                  step="0.01"
                  className={`${shopStyles.deliveryInput} ${errors[errorKey] ? 'error' : ''}`}
                />
                <span className={shopStyles.currencyLabel}>{user?.business?.currency || null}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const isReturnTab = activeTab === 'return';

  return (
    <div className={modalStyles.modalOverlay} onClick={onClose}>
      <div className={`${modalStyles.modal} ${styles.wideModal}`} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.modalHeader}>
          <h2>
            <Banknote size={20} />
            {t('couriers.deliveryChargesModalTitle')} {courier.accountName}
          </h2>
          <button onClick={onClose} className={modalStyles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={modalStyles.modalContent}>
          {loading ? (
            <div className={styles.loadingState}>
              <RefreshCw size={18} className={styles.spinning} />
              {t('couriers.deliveryChargesLoading')}
            </div>
          ) : (
            <>
              <div className={styles.tabs}>
                {KINDS.map(kind => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setActiveTab(kind)}
                    className={`${styles.tab} ${activeTab === kind ? styles.activeTab : ''}`}
                  >
                    {t(kind === 'return' ? 'couriers.returnChargesTab' : 'couriers.deliveryChargesTab')}
                  </button>
                ))}
              </div>

              <p className={styles.description}>
                {t(isReturnTab ? 'couriers.returnChargesModalDesc' : 'couriers.deliveryChargesModalDesc')}
              </p>

              <div className={styles.toolbar}>
                <div className={shopStyles.bulkActions}>
                  <button type="button" onClick={() => handleBulkEditOpen(activeTab)} className={shopStyles.bulkEditButton}>
                    <Edit3 size={16} />
                    {t('shops.bulkEdit')}
                  </button>
                  <button type="button" onClick={() => handleSetFreeAll(activeTab)} className={shopStyles.freeDeliveryButton}>
                    <Package size={16} />
                    {t(isReturnTab ? 'couriers.setFreeReturnAll' : 'shops.setFreeDeliveryAll')}
                  </button>
                </div>
              </div>

              {renderZoneInputs(activeTab, 'amount')}
              {errors[`${activeTab}Charges`] && (
                <span className={shopStyles.errorText}>{errors[`${activeTab}Charges`]}</span>
              )}

              <div className={shopStyles.extraChargesToggle}>
                <label className={shopStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={showExtraCharges[activeTab]}
                    onChange={(e) => toggleExtraCharges(activeTab, e.target.checked)}
                    className={shopStyles.checkbox}
                  />
                  <span>{t('shops.setupExtraCharges')}</span>
                </label>
              </div>

              {showExtraCharges[activeTab] && (
                <div className={shopStyles.extraChargesSection}>
                  <p className={shopStyles.extraChargesDesc}>
                    {t(isReturnTab ? 'couriers.returnExtraChargesDesc' : 'couriers.extraChargesDesc')}
                  </p>
                  {renderZoneInputs(activeTab, 'extra')}
                  {errors[`${activeTab}Extra`] && (
                    <span className={shopStyles.errorText}>{errors[`${activeTab}Extra`]}</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className={modalStyles.modalFooter}>
          <button onClick={onClose} className={modalStyles.secondaryButton} disabled={isSubmitting}>
            {t('couriers.cancel')}
          </button>
          <button onClick={handleSave} className={modalStyles.primaryButton} disabled={isSubmitting || loading}>
            {isSubmitting ? (
              <>
                <RefreshCw size={16} className={modalStyles.spinning} />
                {t('couriers.saving')}
              </>
            ) : (
              <>
                <Save size={16} />
                {t('couriers.saveCharges')}
              </>
            )}
          </button>
        </div>
      </div>

      {bulkEditKind && (
        <div className={shopStyles.dialogOverlay} onClick={(e) => e.stopPropagation()}>
          <div className={shopStyles.dialogContent}>
            <div className={shopStyles.dialogHeader}>
              <Edit3 size={24} />
              <h3>{t(bulkEditKind === 'return' ? 'couriers.bulkEditReturnCharges' : 'shops.bulkEditDeliveryCharges')}</h3>
              <button onClick={() => setBulkEditKind(null)} className={shopStyles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div className={shopStyles.dialogBody}>
              <div className={shopStyles.formGroup}>
                <label htmlFor="courierBulkEditValue">
                  {t(bulkEditKind === 'return' ? 'couriers.returnChargeAmount' : 'shops.deliveryChargeAmount')}
                </label>
                <div className={shopStyles.inputWithIcon}>
                  <Banknote size={18} className={shopStyles.inputIcon} />
                  <input
                    type="number"
                    id="courierBulkEditValue"
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    placeholder="150.00"
                    min="0"
                    step="0.01"
                    className={shopStyles.input}
                    autoFocus
                  />
                  <span className={shopStyles.currencyLabel}>{user?.business?.currency || null}</span>
                </div>
                <span className={shopStyles.formHint}>
                  {t(bulkEditKind === 'return' ? 'couriers.bulkEditReturnHint' : 'shops.bulkEditHint')}
                </span>
              </div>

              <div className={shopStyles.extraChargesToggle}>
                <label className={shopStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={showExtraCharges[bulkEditKind]}
                    onChange={(e) => toggleExtraCharges(bulkEditKind, e.target.checked)}
                    className={shopStyles.checkbox}
                  />
                  <span>{t('shops.setupExtraCharges')}</span>
                </label>
              </div>

              {showExtraCharges[bulkEditKind] && (
                <div className={`${shopStyles.formGroup} ${styles.bulkExtraGroup}`}>
                  <label htmlFor="courierBulkExtraValue">{t('couriers.extraChargePerKgLabel')}</label>
                  <div className={shopStyles.inputWithIcon}>
                    <Banknote size={18} className={shopStyles.inputIcon} />
                    <input
                      type="number"
                      id="courierBulkExtraValue"
                      value={bulkExtraValue}
                      onChange={(e) => setBulkExtraValue(e.target.value)}
                      placeholder={t('shops.extraChargePerKgPlaceholder')}
                      min="0"
                      step="0.01"
                      className={shopStyles.input}
                    />
                    <span className={shopStyles.currencyLabel}>{user?.business?.currency || null}</span>
                  </div>
                  <span className={shopStyles.formHint}>{t('couriers.bulkEditExtraHint')}</span>
                </div>
              )}
            </div>
            <div className={shopStyles.dialogActions}>
              <button type="button" onClick={() => setBulkEditKind(null)} className={shopStyles.cancelButton}>
                {t('common.cancel')}
              </button>
              <button type="button" onClick={handleBulkEditApply} className={shopStyles.submitButton}>
                <Check size={16} />
                {t('shops.applyToAll')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourierDeliveryChargesModal;
